import type { Locale } from '../lib/types'

export type StudioStage =
  | 'idle'
  | 'analyzing'
  | 'priority_prompt'
  | 'opportunity_ready'
  | 'generating_campaign'
  | 'campaign_ready'

export interface QuickAction {
  id: string
  icon: string
  label: Record<Locale, string>
  prompt: Record<Locale, string>
}

export interface WhyExplanation {
  title: Record<Locale, string>
  subtitle: Record<Locale, string>
  points: Record<Locale, string[]>
  sources: string[]
  confidence: 'ALTA' | 'MEDIA' | 'BAJA'
  disclaimer: Record<Locale, string>
}

export interface ChannelItem {
  id: string
  name: string
  priority: 'ALTA' | 'MEDIA'
  explanation: Record<Locale, string>
  badgeVariant: 'success' | 'warning'
}

export interface ContentItem {
  id: string
  name: string
  type: 'instagram' | 'google' | 'landing' | 'email'
  headline: string
  body: string
  cta: string
}

export interface BriefingCardState {
  label: Record<Locale, string>
  detail?: Record<Locale, string>
  subdetail?: Record<Locale, string>
  status: 'definido' | 'recomendado' | 'pendiente'
  confidence?: 'ALTA' | 'MEDIA'
}

export interface DynamicBriefing {
  objective: BriefingCardState
  audience: BriefingCardState
  timing: BriefingCardState
  product: BriefingCardState
  channels: BriefingCardState
}

export interface OpportunityData {
  title: Record<Locale, string>
  subtitle: string
  recommendation: Record<Locale, string>
  potential: 'ALTO' | 'MEDIO'
  confidence: 'ALTA' | 'MEDIA'
  reasons: Record<Locale, string[]>
  why: WhyExplanation
}

export interface CampaignProposalData {
  title: string
  subtitle: Record<Locale, string>
  objective: Record<Locale, string>
  audience: Record<Locale, string>
  period: string
  creativeConcept: string
  mainMessage: string
  cta: string
  channels: ChannelItem[]
  contents: ContentItem[]
}

export const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'product_husky',
    icon: 'Sparkles',
    label: {
      es: 'Tengo producto: Safaris Husky',
      en: 'By Product: Husky Safaris',
      fi: 'Tuote: Koiravaljakkoretket',
    },
    prompt: {
      es: 'Tengo una empresa de safaris con huskies en Levi: ¿a qué mercados y en qué meses me conviene lanzar campaña?',
      en: 'I operate husky safaris in Levi: which source markets and months are best to launch my campaign?',
      fi: 'Tarjoan koiravaljakkoretkiä Levillä: mille markkinoille ja minä kuukausina minun kannattaa suunnata kampanja?',
    },
  },
  {
    id: 'market_germany_low',
    icon: 'Globe',
    label: {
      es: 'Por mercado: Alemania en temporada baja',
      en: 'By Market: Germany in Low Season',
      fi: 'Markkina: Saksa hiljaisella kaudella',
    },
    prompt: {
      es: 'Quiero atraer turistas alemanes durante la temporada baja de primavera y otoño: ¿qué experiencias y productos encajan mejor?',
      en: 'I want to attract German travelers during the off-season (spring and autumn): what experiences and products fit best?',
      fi: 'Haluan houkutella saksalaisia matkailijoita kevään ja syksyn hiljaisina viikkoina: mitkä elämykset ja tuotteet toimivat parhaiten?',
    },
  },
  {
    id: 'season_ruska',
    icon: 'Calendar',
    label: {
      es: 'Por temporada: Llenar septiembre (Ruska)',
      en: 'By Season: Fill September (Ruska)',
      fi: 'Sesonki: Syyskuun ruska-aika',
    },
    prompt: {
      es: 'Quiero maximizar la ocupación de mis cabañas y saunas en septiembre durante el otoño y las primeras auroras.',
      en: 'I want to maximize occupancy for my cabins and saunas in September during autumn colors and early northern lights.',
      fi: 'Haluan maksimoida mökkieni ja saunojeni käyttöasteen syyskuussa ruskan ja ensirevontulien aikaan.',
    },
  },
  {
    id: 'family_uk',
    icon: 'Users',
    label: {
      es: 'Por target: Familias de Reino Unido',
      en: 'By Target: UK Family Market',
      fi: 'Kohderyhmä: Brittiläiset lapsiperheet',
    },
    prompt: {
      es: 'Quiero paquetizar actividades de nieve y alojamiento para familias británicas en vacaciones escolares.',
      en: 'I want to package snow activities and accommodation for British families during school breaks.',
      fi: 'Haluan paketoida talviaktiviteetteja ja majoitusta brittiläisille lapsiperheille talvilomakaudeksi.',
    },
  },
]

export const PRIORITY_OPTIONS = [
  {
    id: 'more_bookings',
    icon: 'TrendingUp',
    label: { es: 'Más reservas', en: 'More bookings', fi: 'Lisää varauksia' },
  },
  {
    id: 'higher_margin',
    icon: 'Coins',
    label: { es: 'Mayor margen', en: 'Higher margin', fi: 'Korkeampi kate' },
  },
  {
    id: 'new_clients',
    icon: 'Users',
    label: { es: 'Nueva clientela', en: 'New visitors', fi: 'Uusia asiakkaita' },
  },
  {
    id: 'low_season',
    icon: 'Snowflake',
    label: { es: 'Temporada baja', en: 'Low season', fi: 'Hiljainen kausi' },
  },
]

export const INITIAL_BRIEFING: DynamicBriefing = {
  objective: {
    label: { es: 'Sin definir', en: 'Undefined', fi: 'Määrittelemätön' },
    detail: {
      es: 'Pendiente de inicio de análisis',
      en: 'Pending analysis start',
      fi: 'Odottaa analyysin aloitusta',
    },
    status: 'pendiente',
  },
  audience: {
    label: { es: 'Pendiente', en: 'Pending', fi: 'Odottaa' },
    status: 'pendiente',
  },
  timing: {
    label: { es: 'Pendiente', en: 'Pending', fi: 'Odottaa' },
    status: 'pendiente',
  },
  product: {
    label: { es: 'Pendiente', en: 'Pending', fi: 'Odottaa' },
    status: 'pendiente',
  },
  channels: {
    label: { es: 'Pendiente', en: 'Pending', fi: 'Odottaa' },
    status: 'pendiente',
  },
}

export const WINTER_FINLAND_OPPORTUNITY: OpportunityData = {
  title: {
    es: 'FINLANDIA · WINTER NATURE',
    en: 'FINLAND · WINTER NATURE',
    fi: 'SUOMI · TALVILUONTO',
  },
  subtitle: 'Diciembre 2026 → Enero 2027',
  recommendation: {
    es: 'Priorizar Finlandia durante diciembre–enero.',
    en: 'Prioritize Finland during December–January.',
    fi: 'Priorisoi Suomi joulu–tammikuun aikana.',
  },
  potential: 'ALTO',
  confidence: 'ALTA',
  reasons: {
    es: [
      'Estacionalidad favorable en demanda doméstica y de proximidad',
      'Mercado emisor consolidado con menor coste de captación',
      'Interés preferente en naturaleza y desconexión ártica',
      'Oferta disponible y sin saturación en semanas valle',
    ],
    en: [
      'Favorable seasonality in domestic and proximity demand',
      'Established source market with lower customer acquisition cost',
      'High affinity for nature and arctic tranquility',
      'Available capacity without peak crowding during valley weeks',
    ],
    fi: [
      'Suotuisa kausiluonteisuus kotimaan ja lähialueen kysynnässä',
      'Vakiintunut lähtömarkkina edullisemmalla hankintakustannuksella',
      'Vahva kiinnostus luontoelämyksiin ja arktiseen rauhoittumiseen',
      'Kapasiteettia saatavilla ilman ruuhkautumista sesonkien välillä',
    ],
  },
  why: {
    title: {
      es: '¿Por qué Finlandia?',
      en: 'Why Finland?',
      fi: 'Miksi Suomi?',
    },
    subtitle: {
      es: 'Evaluación explicable del motor NADIA',
      en: 'Explainable assessment by NADIA Engine',
      fi: 'NADIA-moottorin läpinäkyvä arvio',
    },
    points: {
      es: [
        'Demanda estacional favorable con reservas anticipadas en el mercado nacional.',
        'Interés probado en naturaleza, bienestar ártico y actividades al aire libre.',
        'Afinidad con experiencias de invierno no masificadas en Levi / Sirkka.',
        'Potencial emisor identificado en registros de conectividad terrestre y vuelos Kittilä.',
      ],
      en: [
        'Favorable seasonal demand with advance bookings from the domestic market.',
        'Proven interest in nature, arctic wellness, and outdoor adventures.',
        'High affinity for serene, non-crowded winter activities in Levi / Sirkka.',
        'Source market potential identified via ground travel and Kittilä flight connectivity.',
      ],
      fi: [
        'Suotuisa kausikysyntä ja ennakkovaraukset kotimarkkinoilta.',
        'Osoitettu kiinnostus luontoa, arktista hyvinvointia ja ulkoilua kohtaan.',
        'Vahva mieltymys rauhallisiin talvielämyksiin Levillä ja Sirkassa.',
        'Lähtömarkkinapotentiaali tunnistettu maaliikenteen ja Kittilän lentoyhteyksien datasta.',
      ],
    },
    sources: [
      'Statistics Finland (noches de hotel y pernoctaciones)',
      'OpenStreetMap (equipamientos y POIs Levi/Sirkka)',
      'DEPLOYTOUR · Visit Levi Pilot',
    ],
    confidence: 'ALTA',
    disclaimer: {
      es: 'Datos consolidados y calibrados para demostración del MVP de DEPLOYTOUR.',
      en: 'Consolidated and calibrated figures for DEPLOYTOUR MVP demonstration.',
      fi: 'Koostettu ja kalibroitu aineisto DEPLOYTOUR-pilotin MVP-esittelyä varten.',
    },
  },
}

export const CAMPAIGN_PROPOSAL_MOCK: CampaignProposalData = {
  title: 'Winter Nature · Finland',
  subtitle: {
    es: 'Campaña recomendada por NADIA Marketing Assistant',
    en: 'Recommended campaign by NADIA Marketing Assistant',
    fi: 'NADIA-markkinointiavustajan suosittelema kampanja',
  },
  objective: {
    es: 'Aumentar reservas durante temporada baja.',
    en: 'Increase bookings during low season.',
    fi: 'Lisätä varauksia hiljaisemman sesongin aikana.',
  },
  audience: {
    es: 'Familias finlandesas 35–54 interesadas en naturaleza y actividades familiares.',
    en: 'Finnish families 35–54 interested in nature and family activities.',
    fi: 'Suomalaiset perheet 35–54 v, jotka ovat kiinnostuneita luonto- ja perheaktiviteeteista.',
  },
  period: '15 diciembre 2026 → 31 enero 2027',
  creativeConcept: 'Discover Lapland beyond the snow',
  mainMessage: 'Experience the quiet side of Lapland with your family.',
  cta: 'Discover winter experiences',
  channels: [
    {
      id: 'instagram',
      name: 'Instagram',
      priority: 'ALTA',
      explanation: {
        es: 'Visual storytelling y segmentación por intereses en viajes familiares y naturaleza.',
        en: 'Visual storytelling and interest targeting for family travel and nature.',
        fi: 'Visuaalinen tarinankerronta ja kohdennus perhematkailusta ja luonnosta kiinnostuneille.',
      },
      badgeVariant: 'success',
    },
    {
      id: 'google_ads',
      name: 'Google Ads',
      priority: 'ALTA',
      explanation: {
        es: 'Capturar demanda activa relacionada con experiencias de invierno y escapadas a Levi.',
        en: 'Capture high-intent searches for winter activities and Levi getaways.',
        fi: 'Tavoita aktiivinen haku talvielämyksille ja Levin matkoille.',
      },
      badgeVariant: 'success',
    },
    {
      id: 'tourist_web',
      name: 'Web turística',
      priority: 'MEDIA',
      explanation: {
        es: 'Página de destino propia con conversión directa e integración con motores de reserva.',
        en: 'Dedicated landing page with direct conversion and booking engine integration.',
        fi: 'Oma laskeutumissivu suoralla konversiolla ja varausjärjestelmäintegraatiolla.',
      },
      badgeVariant: 'warning',
    },
    {
      id: 'facebook',
      name: 'Facebook',
      priority: 'MEDIA',
      explanation: {
        es: 'Campañas de retargeting para familias finlandesas de 35 a 54 años.',
        en: 'Retargeting campaigns focused on Finnish families aged 35 to 54.',
        fi: 'Uudelleenkohdennuskampanjat 35–54-vuotiaille suomalaisperheille.',
      },
      badgeVariant: 'warning',
    },
  ],
  contents: [
    {
      id: 'instagram_ad',
      name: 'Instagram Ad',
      type: 'instagram',
      headline: 'Discover another side of Lapland',
      body: 'Nature, winter and unforgettable family experiences in Levi.',
      cta: 'Discover more',
    },
    {
      id: 'google_ad',
      name: 'Google Ad',
      type: 'google',
      headline: 'Levi Winter Nature & Family Breaks',
      body: 'Book authentic arctic experiences, serene fells & family winter adventures.',
      cta: 'Book now',
    },
    {
      id: 'landing_page',
      name: 'Landing Page',
      type: 'landing',
      headline: 'Quiet Arctic Winter in Sirkka & Levi Fell',
      body: 'Curated winter packages: husky safaris, snowshoe trails, family cabins and northern light hunts.',
      cta: 'Explore packages',
    },
    {
      id: 'email_campaign',
      name: 'Email Campaign',
      type: 'email',
      headline: 'Your peaceful Lapland winter escape awaits',
      body: 'Exclusive early-bird itineraries for families seeking nature without the crowds this December.',
      cta: 'Plan your stay',
    },
  ],
}
